import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { supabase, readableError } from '../../lib/supabase'
import { useSession } from '../../lib/session'
import { toTitleCase } from '../../lib/format'
import { Shell } from '../../components/Shell'
import { Button, Field, Spinner, ToggleRow, avatarTone, initials } from '../../components/ui'

/**
 * The church's page, from the inside — the one admin screen that is about how
 * the church looks outward rather than how it works inward.
 *
 * Curation only. The database decides what a church is *allowed* to feature
 * (a song by its own writer, already out at Collective reach; a person who is
 * actually a member), so this screen's job is to offer good choices and to
 * pass the refusal through in plain words when a pastor picks something the
 * writer hasn't opened up yet.
 *
 * Nothing here is live until the publish switch is on. A half-built page that
 * strangers can already see is the thing a pastor would most reasonably be
 * annoyed by.
 */
const writerName = (w) => [w?.first_name, w?.last_name].filter(Boolean).join(' ')

export default function ChurchPageEditor() {
  const { church } = useSession()
  const id = church?.id

  const [row, setRow] = useState(null)          // the editable church columns
  const [feats, setFeats] = useState([])
  const [songMap, setSongMap] = useState({})
  const [writerMap, setWriterMap] = useState({})
  const [ready, setReady] = useState(false)
  const [err, setErr] = useState(null)
  const [busy, setBusy] = useState(false)
  const [saved, setSaved] = useState(false)

  // The two pickers, and the link form.
  const [picking, setPicking] = useState(null)  // 'song' | 'writer' | 'link' | null
  const [roster, setRoster] = useState([])
  const [pickSongs, setPickSongs] = useState([])
  const [linkTitle, setLinkTitle] = useState('')
  const [linkUrl, setLinkUrl] = useState('')
  const [linkNote, setLinkNote] = useState('')

  const load = useCallback(async () => {
    if (!id) return setReady(true)
    const [{ data: c }, { data: f }] = await Promise.all([
      supabase.from('churches')
        .select('id, name, tagline, bio, website_url, profile_published')
        .eq('id', id).maybeSingle(),
      supabase.from('church_features').select('*').eq('church_id', id)
        .order('position', { ascending: true }).order('created_at', { ascending: true }),
    ])
    setRow(c ?? null)
    const rows = f ?? []
    setFeats(rows)

    const songIds = rows.filter((r) => r.song_id).map((r) => r.song_id)
    const writerIds = rows.filter((r) => r.writer_id).map((r) => r.writer_id)
    const [{ data: sg }, { data: wr }] = await Promise.all([
      songIds.length
        ? supabase.from('songs').select('id, title').in('id', songIds)
        : Promise.resolve({ data: [] }),
      writerIds.length
        ? supabase.from('songwriter_profiles').select('id, first_name, last_name, avatar_url')
            .in('id', writerIds)
        : Promise.resolve({ data: [] }),
    ])
    setSongMap(Object.fromEntries((sg ?? []).map((s) => [s.id, s])))
    setWriterMap(Object.fromEntries((wr ?? []).map((w) => [w.id, w])))
    setReady(true)
  }, [id])

  useEffect(() => { load() }, [load])

  const patch = async (fields) => {
    setErr(null); setBusy(true)
    const { error } = await supabase.from('churches').update(fields).eq('id', id)
    setBusy(false)
    if (error) return setErr(readableError(error))
    setRow((r) => ({ ...r, ...fields }))
    setSaved(true)
    setTimeout(() => setSaved(false), 1600)
  }

  /** The roster, for both pickers. Only approved members: the database will
   *  refuse anyone else anyway, and offering a name that then bounces is a
   *  worse experience than not offering it. */
  const openPicker = async (kind) => {
    setErr(null); setPicking(kind)
    if (kind === 'link') return
    const { data: members } = await supabase.from('church_members')
      .select('user_id, verified, users:user_id(id, first_name, last_name, avatar_url)')
      .eq('church_id', id).eq('membership_status', 'approved')
    const people = (members ?? []).filter((m) => m.users)
    setRoster(people)
    if (kind === 'song') {
      const ids = people.map((m) => m.user_id)
      if (!ids.length) return setPickSongs([])
      const { data } = await supabase.from('songs')
        .select('id, title, songwriter_id')
        .in('songwriter_id', ids).eq('reach', 'collective').is('removed_at', null)
        .order('title')
      setPickSongs(data ?? [])
    }
  }

  const add = async (fields) => {
    setErr(null); setBusy(true)
    const position = feats.length ? Math.max(...feats.map((f) => f.position)) + 1 : 0
    const { error } = await supabase.from('church_features')
      .insert({ church_id: id, position, ...fields })
    setBusy(false)
    if (error) return setErr(readableError(error))
    setPicking(null); setLinkTitle(''); setLinkUrl(''); setLinkNote('')
    load()
  }

  const remove = async (featId) => {
    setErr(null)
    const { error } = await supabase.from('church_features').delete().eq('id', featId)
    if (error) return setErr(readableError(error))
    setFeats((f) => f.filter((x) => x.id !== featId))
  }

  /** Swap two rows' positions. Renumbering the whole list on every nudge would
   *  be tidier in the table and slower on the screen; two updates is enough. */
  const move = async (index, delta) => {
    const a = feats[index]
    const b = feats[index + delta]
    if (!a || !b) return
    setErr(null)
    const [{ error: e1 }, { error: e2 }] = await Promise.all([
      supabase.from('church_features').update({ position: b.position }).eq('id', a.id),
      supabase.from('church_features').update({ position: a.position }).eq('id', b.id),
    ])
    if (e1 || e2) return setErr(readableError(e1 || e2))
    load()
  }

  if (!ready) return <Shell back="/admin/people" tabs><Spinner /></Shell>

  if (!id || !row) {
    return (
      <Shell back="/admin/people" tabs>
        <h2 className="q">No church here yet.</h2>
        <p className="sub">Set your church up first, then this page will have something to show.</p>
      </Shell>
    )
  }

  const label = (f) => {
    if (f.kind === 'song') return toTitleCase(songMap[f.song_id]?.title || '') || 'Song'
    if (f.kind === 'writer') return writerName(writerMap[f.writer_id]) || 'Writer'
    return f.title
  }
  const sub = (f) => (f.kind === 'link' ? f.url : f.note || (f.kind === 'song' ? 'Song' : 'Writer'))

  const alreadyOn = (key, value) => feats.some((f) => f[key] === value)

  return (
    <Shell back="/admin/people" tabs>
      <h2 className="q">Your church page</h2>
      <p className="sub">
        What writers and other churches see when they land on {row.name}. You choose
        every item on it.
      </p>

      {err && <p className="err">{err}</p>}

      <div className="rows" style={{ marginBottom: 18 }}>
        <ToggleRow
          title="Show this page"
          hint={row.profile_published
            ? 'Anyone signed in to Beseter can see it.'
            : 'Off — only your own staff can see it while you build it up.'}
          on={!!row.profile_published}
          disabled={busy}
          onChange={(v) => patch({ profile_published: v })}
        />
      </div>

      <Field label="One line about you">
        <input className="input" maxLength={90} defaultValue={row.tagline || ''}
               placeholder="A church in the inner west, writing our own songs"
               onBlur={(e) => e.target.value !== (row.tagline || '') && patch({ tagline: e.target.value || null })} />
      </Field>

      <Field label="A bit more">
        <textarea className="input" rows={4} maxLength={600} defaultValue={row.bio || ''}
                  placeholder="Who you are, what your worship team is like, what you're looking for."
                  onBlur={(e) => e.target.value !== (row.bio || '') && patch({ bio: e.target.value || null })} />
      </Field>

      <Field label="Website">
        <input className="input" type="url" defaultValue={row.website_url || ''}
               placeholder="https://"
               onBlur={(e) => e.target.value !== (row.website_url || '') && patch({ website_url: e.target.value || null })} />
      </Field>
      {saved && <p className="small" style={{ color: 'var(--accent-deep)' }}>Saved.</p>}

      <h3 className="sechead">
        On the page
        <span className="note">In this order. Songs and writers come from your own people.</span>
      </h3>

      {feats.length === 0 && (
        <p className="small">Nothing yet — add a song, a writer, or a link below.</p>
      )}

      <div className="rows">
        {feats.map((f, i) => (
          <div key={f.id} className="action col">
            <div className="row">
              <span>
                {label(f)}
                <small>{sub(f)}</small>
              </span>
              <span style={{ display: 'flex', gap: 6 }}>
                <button className="iconbtn" disabled={i === 0}
                        onClick={() => move(i, -1)} aria-label="Move up">↑</button>
                <button className="iconbtn" disabled={i === feats.length - 1}
                        onClick={() => move(i, 1)} aria-label="Move down">↓</button>
                <button className="iconbtn" onClick={() => remove(f.id)} aria-label="Remove">✕</button>
              </span>
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '14px 0 8px' }}>
        <button className="btn" onClick={() => openPicker('song')}>Add a song</button>
        <button className="btn" onClick={() => openPicker('writer')}>Add a writer</button>
        <button className="btn" onClick={() => openPicker('link')}>Add a link</button>
      </div>

      {picking === 'song' && (
        <>
          <h3 className="sechead">
            Pick a song
            <span className="note">
              Only songs your writers have opened to the Collective. If one you want
              isn’t here, ask the writer — it’s their call, not yours.
            </span>
          </h3>
          {pickSongs.length === 0 ? (
            <p className="small">Nothing to pick yet.</p>
          ) : (
            <div className="rows">
              {pickSongs.map((s) => (
                <button key={s.id} className="action" disabled={busy || alreadyOn('song_id', s.id)}
                        onClick={() => add({ kind: 'song', song_id: s.id })}>
                  <span>
                    {toTitleCase(s.title) || 'Untitled'}
                    {alreadyOn('song_id', s.id) && <small>Already on your page</small>}
                  </span>
                  <span className="ar">+</span>
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {picking === 'writer' && (
        <>
          <h3 className="sechead">
            Pick a writer
            <span className="note">People in your church. Their profile does the talking.</span>
          </h3>
          {roster.length === 0 ? (
            <p className="small">No approved members yet.</p>
          ) : (
            <div className="rows">
              {roster.map((m) => (
                <button key={m.user_id} className="action"
                        disabled={busy || alreadyOn('writer_id', m.user_id)}
                        onClick={() => add({ kind: 'writer', writer_id: m.user_id })}>
                  <span className="ch-writer">
                    {m.users.avatar_url
                      ? <img className="wp-avatar" src={m.users.avatar_url} alt="" />
                      : <span className={`wp-avatar ${avatarTone(m.user_id)}`}>
                          {initials(writerName(m.users))}
                        </span>}
                    <span>
                      {writerName(m.users) || 'Writer'}
                      {m.verified && <small>You’ve vouched for them</small>}
                      {alreadyOn('writer_id', m.user_id) && <small>Already on your page</small>}
                    </span>
                  </span>
                  <span className="ar">+</span>
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {picking === 'link' && (
        <>
          <h3 className="sechead">
            Add a link
            <span className="note">
              A Sunday recording on YouTube or Vimeo — far better than trying to
              upload the file. Must start with https://
            </span>
          </h3>
          <Field label="What is it">
            <input className="input" maxLength={80} value={linkTitle}
                   placeholder="Live from our Easter service"
                   onChange={(e) => setLinkTitle(e.target.value)} />
          </Field>
          <Field label="Link">
            <input className="input" type="url" value={linkUrl} placeholder="https://"
                   onChange={(e) => setLinkUrl(e.target.value)} />
          </Field>
          <Field label="A note (optional)">
            <input className="input" maxLength={80} value={linkNote}
                   onChange={(e) => setLinkNote(e.target.value)} />
          </Field>
          <Button busy={busy} disabled={!linkTitle.trim() || !/^https:\/\//i.test(linkUrl)}
                  onClick={() => add({
                    kind: 'link', title: linkTitle.trim(), url: linkUrl.trim(),
                    note: linkNote.trim() || null,
                  })}>
            Add it
          </Button>
        </>
      )}

      <p className="small" style={{ marginTop: 22 }}>
        <Link to={`/church/${id}`}>See it the way a visitor does ›</Link>
      </p>
    </Shell>
  )
}
